
import Vue from "vue";
import { NewToiletsApiFunc, NewBoxesApiFunc, setAccessToken, NewGatewaysApiFunc } from "@/api";
import { Toilet, ToiletType, ToiletStatus, Box, Gateway } from "@/models";

import TheTemplate from "../../TheNoSidebarTemplate.vue";
import ToiletBoxList from "./components/ToiletBoxList.vue";
import Pager from "@/components/BasePager.vue";
import BaseCloseButton from "@/components/BaseCloseButton.vue";

export type DataType = {
  toilet?: Toilet;
  boxes: Array<Box>;
  totalPage: number;
  currentPage: number;
  defaultIntervalId: number | undefined;
  currentPageIntervalId: number | undefined;
  gateway?: Gateway;
  fetchGatewayIntervalId: number | undefined;
};

const SIX_SECONDS = 6000;

export default Vue.extend({
  name: "ToiletBoxListScreen",

  components: {
    TheTemplate,
    ToiletBoxList,
    Pager,
    BaseCloseButton,
  },

  data(): DataType {
    return {
      toilet: undefined,
      boxes: [],
      totalPage: 0,
      currentPage: 0,
      defaultIntervalId: undefined,
      currentPageIntervalId: undefined,
      gateway: undefined,
      fetchGatewayIntervalId: undefined,
    };
  },

  mounted() {
    this.getToilet()
      .then(() => this.getGateway())
      .then(() => (this.fetchGatewayIntervalId = window.setInterval(() => this.getGateway(), SIX_SECONDS)));
    this.fetchBoxes();
    this.defaultIntervalId = window.setInterval(() => this.fetchBoxes(), SIX_SECONDS);
  },

  beforeDestroy() {
    window.clearInterval(this.fetchGatewayIntervalId);
  },

  watch: {
    $route(to, from) {
      if (to.params.updated === "true") {
        this.getToilet();
        this.fetchBoxes(this.currentPage);
      }
    },
  },

  computed: {
    floorPlanUrl(): string | undefined {
      return this.toilet?.floorPlanUrl;
    },
    isAdmin(): boolean {
      return this.$auth.role === "admin";
    },
    toiletTypeText(): string {
      switch (this.toilet?.toiletType) {
        case ToiletType.FOR_MAN:
          return "男性トイレ";
        case ToiletType.FOR_WOMAN:
          return "女性トイレ";
        case ToiletType.UNISEX:
          return "共用";
        case ToiletType.MULTI_PURPOSE:
          return "多目的トイレ";
        default:
          return "不明";
      }
    },

    numOfBoxes(): string {
      return String(this.toilet?.boxesCount || 0);
    },

    toiletStatusText(): string {
      switch (this.toilet?.status) {
        case ToiletStatus.RUNNING:
          return "稼動";
        case ToiletStatus.IN_MAINTENANCE:
          return "メンテ中";
        case ToiletStatus.IN_PREPARING:
          return "準備中";
        default:
          return "不明";
      }
    },

    toiletStatusClassText(): string {
      switch (this.toilet?.status) {
        case ToiletStatus.RUNNING:
          return "";
        case ToiletStatus.IN_MAINTENANCE:
          return "red";
        case ToiletStatus.IN_PREPARING:
          return "red";
        default:
          return "red";
      }
    },
  },

  methods: {
    async getToilet(): Promise<void> {
      const companyId = this.$auth.companyId;
      if (!companyId) {
        this.$router.replace({ name: "login" });
        return;
      }
      const toiletId = this.$router.currentRoute.params.toiletId;
      setAccessToken(this.$auth.accessToken);
      const { data, err } = await NewToiletsApiFunc().getToilet(companyId, toiletId);

      if (err != null) {
        this.$handleApiError(err);
        return;
      }

      this.toilet = data;
    },

    async getGateway(): Promise<void> {
      if (this.toilet === undefined) {
        return;
      }
      const companyId = this.$auth.companyId;
      if (!companyId) {
        this.$router.replace({ name: "login" });
        return;
      }
      setAccessToken(this.$auth.accessToken);

      const gatewayId = this.toilet.gatewayId;
      if (gatewayId === undefined) {
        return;
      }
      const { data, err } = await NewGatewaysApiFunc().adminGetGateway(companyId, gatewayId);
      if (err !== undefined) {
        this.$handleApiError(err);
        return;
      }
      this.gateway = data;
    },

    async fetchBoxes(page?: number): Promise<void> {
      const companyId = this.$auth.companyId;
      if (!companyId) {
        this.$router.replace({ name: "login" });
        return;
      }
      const toiletId = this.$router.currentRoute.params.toiletId;
      setAccessToken(this.$auth.accessToken);
      const { data, err } = await NewBoxesApiFunc().listBoxes(companyId, toiletId, undefined, page);

      if (err != null) {
        this.$handleApiError(err);
        return;
      }

      this.boxes = data.list;
      this.currentPage = data.currentPage;
      this.totalPage = data.totalPage;
    },

    changePage(page: number): void {
      window.clearInterval(this.defaultIntervalId);
      this.defaultIntervalId = undefined;
      window.clearInterval(this.currentPageIntervalId);
      this.currentPageIntervalId = undefined;
      this.fetchBoxes(page);
      this.currentPageIntervalId = window.setInterval(() => this.fetchBoxes(), SIX_SECONDS);
    },

    back(): void {
      this.$router.push({
        name: "company-toilets",
        params: {
          updated: "true",
        },
      });
    },
  },
});
